import React, { useState, useEffect } from 'react';
import './LogbookView.css';

function PhotoLightbox({ src, alt, onClose }) {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="logbook-lightbox"
      onClick={onClose}
      role="dialog"
      aria-label="Photo viewer"
      aria-modal="true"
    >
      <div className="logbook-lightbox__content" onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={alt} className="logbook-lightbox__img" />
        <button
          className="logbook-lightbox__close"
          onClick={onClose}
          aria-label="Close photo"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function EntryPhotos({ photoIds, getPhotos }) {
  const [photoUrls, setPhotoUrls] = useState([]);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    let revoked = false;
    const urls = [];

    async function loadPhotos() {
      if (!getPhotos || photoIds.length === 0) return;
      const photos = await getPhotos(photoIds);
      if (revoked) return;

      const objectUrls = photos
        .filter(Boolean)
        .map((photo) => {
          const url = URL.createObjectURL(photo.blob);
          urls.push(url);
          return url;
        });
      setPhotoUrls(objectUrls);
    }

    loadPhotos();

    return () => {
      revoked = true;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photoIds, getPhotos]);

  if (photoUrls.length === 0) return null;

  return (
    <div className="logbook-view__entry-images">
      {photoUrls.map((url, idx) => (
        <button
          key={idx}
          type="button"
          className="logbook-view__entry-img-btn"
          onClick={() => setLightboxUrl(url)}
          aria-label={`View photo ${idx + 1} full size`}
        >
          <img
            src={url}
            alt={`Log photo ${idx + 1}`}
            className="logbook-view__entry-img"
          />
        </button>
      ))}
      {lightboxUrl && (
        <PhotoLightbox
          src={lightboxUrl}
          alt="Full size log photo"
          onClose={() => setLightboxUrl(null)}
        />
      )}
    </div>
  );
}

function LogbookView({ entries, onAddEntry, plantName, getPhotos }) {
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="logbook-view">
      <div className="logbook-view__header">
        <h2 className="logbook-view__title">Logbook: {plantName}</h2>
        <button
          type="button"
          className="logbook-view__add-btn"
          onClick={onAddEntry}
        >
          Add Entry
        </button>
      </div>

      {entries.length === 0 ? (
        <p className="logbook-view__empty">
          No log entries yet. Add your first entry to start tracking this plant.
        </p>
      ) : (
        <ul className="logbook-view__list">
          {entries.map((entry) => {
            const isEdit = entry.type === 'edit' || (entry.notes && entry.notes.startsWith('[Edit]'));
            return (
              <li key={entry.id} className={`logbook-view__entry ${isEdit ? 'logbook-view__entry--edit' : ''}`}>
                <div className="logbook-view__entry-header">
                  <span className="logbook-view__entry-date">
                    {formatDate(entry.timestamp)}
                  </span>
                  {isEdit ? (
                    <span className="logbook-view__entry-badge logbook-view__entry-badge--edit">✏️ Edit</span>
                  ) : (
                    <span className="logbook-view__entry-photos">
                      📷 {entry.photoIds.length} photo{entry.photoIds.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {entry.notes && (
                  isEdit ? (
                    <div className="logbook-view__entry-diff">
                      {entry.notes.replace('[Edit]\n', '').split('\n').map((line, i) => (
                        <p key={i} className="logbook-view__diff-line">{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="logbook-view__entry-notes">{entry.notes}</p>
                  )
                )}
                {!isEdit && <EntryPhotos photoIds={entry.photoIds} getPhotos={getPhotos} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default LogbookView;
